import { useEffect, useState } from "react";
import { usePlanStore } from "../stores/planStore";
import type { PlannedSession } from "@runlens/shared";
import {
  CoachChat,
  EmptyPlanState,
  PlanCalendar,
  PlanHeader,
  SessionDetailModal,
} from "../components/planner";

export function TrainingPlannerPage() {
  const {
    activePlan,
    sessions,
    metrics,
    plans,
    isLoading,
    isLoadingPlans,
    error,
    fetchPlans,
    fetchPlan,
    createPlan,
    startCoachConversation,
    resetPlan,
    archivePlan,
  } = usePlanStore();

  const [selectedSession, setSelectedSession] = useState<PlannedSession | null>(null);

  // Load plans on mount
  useEffect(() => {
    fetchPlans();
  }, [fetchPlans]);

  // Auto-select first active plan if none selected
  useEffect(() => {
    if (!activePlan && plans.length > 0 && !isLoadingPlans) {
      const activeplan = plans.find((p) => p.status === "active");
      if (activeplan) {
        fetchPlan(activeplan.id);
      }
    }
  }, [activePlan, plans, isLoadingPlans, fetchPlan]);

  const handleCreatePlan = async (name: string) => {
    const plan = await createPlan(name);
    // Start the coaching conversation after creating the plan
    await startCoachConversation();
    return plan;
  };

  const handleDeletePlan = async () => {
    if (activePlan) {
      await archivePlan(activePlan.id);
    }
  };

  // Loading state
  if (isLoadingPlans && plans.length === 0) {
    return (
      <div className="h-full flex flex-col">
        <h1 className="text-2xl font-bold text-gray-900 mb-4 shrink-0">
          Training Planner
        </h1>
        <div className="flex-1 flex items-center justify-center">
          <div className="flex items-center gap-2 text-gray-500">
            <svg
              className="animate-spin h-5 w-5"
              fill="none"
              viewBox="0 0 24 24"
            >
              <circle
                className="opacity-25"
                cx="12"
                cy="12"
                r="10"
                stroke="currentColor"
                strokeWidth="4"
              />
              <path
                className="opacity-75"
                fill="currentColor"
                d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
              />
            </svg>
            Loading...
          </div>
        </div>
      </div>
    );
  }

  // No active plan - show empty state
  if (!activePlan && !isLoading) {
    const hasPlans = plans.filter((p) => p.status === "active").length > 0;

    if (!hasPlans) {
      return (
        <div className="h-full flex flex-col">
          <h1 className="text-2xl font-bold text-gray-900 mb-4 shrink-0">
            Training Planner
          </h1>
          <div className="flex-1">
            <EmptyPlanState onCreatePlan={handleCreatePlan} />
          </div>
        </div>
      );
    }
  }

  // Loading plan details
  if (isLoading && !activePlan) {
    return (
      <div className="h-full flex flex-col">
        <h1 className="text-2xl font-bold text-gray-900 mb-4 shrink-0">
          Training Planner
        </h1>
        <div className="flex-1 flex items-center justify-center">
          <div className="flex items-center gap-2 text-gray-500">
            <svg
              className="animate-spin h-5 w-5"
              fill="none"
              viewBox="0 0 24 24"
            >
              <circle
                className="opacity-25"
                cx="12"
                cy="12"
                r="10"
                stroke="currentColor"
                strokeWidth="4"
              />
              <path
                className="opacity-75"
                fill="currentColor"
                d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
              />
            </svg>
            Loading plan...
          </div>
        </div>
      </div>
    );
  }

  // Error state
  if (error) {
    return (
      <div className="h-full flex flex-col">
        <h1 className="text-2xl font-bold text-gray-900 mb-4 shrink-0">
          Training Planner
        </h1>
        <div className="flex-1 flex items-center justify-center">
          <div className="text-center">
            <p className="text-red-600 mb-4">{error}</p>
            <button
              type="button"
              onClick={() => fetchPlans()}
              className="px-4 py-2 bg-brand-600 text-white rounded-lg hover:bg-brand-700"
            >
              Try Again
            </button>
          </div>
        </div>
      </div>
    );
  }

  // Active plan view
  return (
    <div className="h-full flex flex-col gap-4 overflow-hidden">
      <h1 className="text-2xl font-bold text-gray-900 shrink-0">
        Training Planner
      </h1>

      {activePlan && (
        <>
          {/* Plan header + Calendar side by side */}
          <div className="shrink-0 flex gap-4 items-stretch">
            <div className="flex-1">
              <PlanHeader plan={activePlan} sessions={sessions} metrics={metrics} onReset={resetPlan} onDelete={handleDeletePlan} />
            </div>
            <div className="w-[768px] shrink-0">
              <PlanCalendar sessions={sessions} onSessionClick={setSelectedSession} />
            </div>
          </div>

          {/* Coach chat */}
          <div className="flex-1 min-h-0">
            <CoachChat />
          </div>

          {/* Session detail modal */}
          {selectedSession && (
            <SessionDetailModal
              session={selectedSession}
              onClose={() => setSelectedSession(null)}
            />
          )}
        </>
      )}
    </div>
  );
}
